/**
 * useUpgradeGate — gate a UI action behind a tier cap.
 *
 * The hook compares the user's current tier + supplied context against the
 * shared `GATES` config. When the action is NOT allowed, `promptUpgrade()`
 * opens the global <UpgradePromptModal>; when it IS allowed, the component
 * can proceed with the original action.
 *
 * Primary UX pattern: buttons stay visible + clickable. Clicks either
 * execute the action or open the upgrade prompt — no hidden controls.
 *
 * Example:
 *   const gate = useUpgradeGate('invite-member', { memberCount });
 *   <Button onClick={gate.allowed ? submit : gate.promptUpgrade}>
 *     Invite teammate
 *   </Button>
 */

import { useMemo, useCallback } from 'react';
import { GATES } from '@bower/shared/billing/gates';
import type { GateKey, GateContext, SubscriptionTier } from '@bower/shared';
import { useWorkspacePlan } from '../data/useBilling';
import { useUpgradePromptStore } from '../../stores/upgradePromptStore';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

export interface UpgradeGateResult {
  /** The user's current tier. 'FREE' while plan data is loading. */
  tier: SubscriptionTier;
  /** Whether the action is allowed at the current tier + context. */
  allowed: boolean;
  /** The tier the user would need to upgrade to. */
  requiredTier: SubscriptionTier;
  /** Opens the global upgrade prompt for this gate. */
  promptUpgrade: () => void;
}

export function useUpgradeGate(gateKey: GateKey, context: GateContext = {}): UpgradeGateResult {
  const { data: plan } = useWorkspacePlan();
  const openPrompt = useUpgradePromptStore((s) => s.open);

  const tier = (plan?.tier ?? 'FREE') as SubscriptionTier;
  const gate = GATES[gateKey];

  const allowed = useMemo(() => gate.check(tier, context), [gate, tier, context]);

  const promptUpgrade = useCallback(() => {
    // Fire the "blocked by tier cap" event BEFORE opening the modal. Both
    // paths (pre-emptive client-side and server-side 402) use the same
    // BILLING_GATE_BLOCKED event; the `source` property distinguishes them
    // so we can measure drop-off from pre-emptive blocks vs. actual attempts.
    safeAnalytics.track(ANALYTICS_EVENTS.BILLING_GATE_BLOCKED, {
      gate_key: gateKey,
      current_tier: tier,
      required_tier: gate.requiredTier,
      source: 'client-preemptive',
    } as never);
    openPrompt(gateKey, context);
  }, [openPrompt, gateKey, context, tier, gate.requiredTier]);

  return {
    tier,
    allowed,
    requiredTier: gate.requiredTier,
    promptUpgrade,
  };
}
